/**
 * SchedulesTableView – AG Grid column definitions and rendering for the Schedules tab.
 * Matches Figma Design 2: Scan Name, Type, Frequency, Created By,
 * Scheduled Time (with inline status badge), Number of Assets, Agent, Date, Actions (kebab).
 */

import React, { useState, useMemo } from "react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getScheduleStatusStyle, formatScanType } from "./scansV2.utils";
import InfoIcon from '@mui/icons-material/Info';
import type { ScheduleRow } from "./scansV2.types";

// ──────────────────────────────────────────────
// Cell Renderers
// ──────────────────────────────────────────────

/** Scan Name column with tooltip */
const ScanNameCellRenderer = (params: ICellRendererParams) => {
  if (!params.value) return null;
  return (
    <Tooltip
      title={params.value}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #ddd",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
          },
        },
        arrow: { sx: { color: "#fff" } },
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {params.value}
      </span>
    </Tooltip>
  );
};

/** Created By column with tooltip */
const CreatedByCellRenderer = (params: ICellRendererParams) => {
  if (!params.value) return null;
  return (
    <Tooltip
      title={params.value}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #ddd",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
          },
        },
        arrow: { sx: { color: "#fff" } },
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {params.value}
      </span>
    </Tooltip>
  );
};

/**
 * Scheduled Time column – shows time followed by an inline status badge.
 * Matches Figma: "12:12:08  ● Active"
 */
const ScheduledTimeCellRenderer = (params: ICellRendererParams) => {
  const data = params.data as ScheduleRow;
  if (!data) return null;

  const time = data.scheduledTime;
  const status = data.scheduledStatus;
  const normalizedStatus = String(status ?? "").trim().toLowerCase();
  const isCanceledOrInactive = normalizedStatus === "cancelled" || normalizedStatus === "canceled" || normalizedStatus === "inactive";
  const mappedStatusForStyle = isCanceledOrInactive ? "Cancelled" : status;
  const style = getScheduleStatusStyle(mappedStatusForStyle as any);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <span>{time}</span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          padding: "0 8px",
          borderRadius: "5px",
          fontWeight: 600,
          fontSize: "12px",
          color: style.color,
          background: style.background,
          border: style.border,
          height: "22px",
          lineHeight: "18px",
          boxSizing: "border-box",
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            backgroundColor: style.color,
            display: "inline-block",
          }}
        />
        {/*
          Display mapping: when backend returns `inactive` or `cancelled`/`canceled` after a cancel action,
          show a more user-friendly label `Cancelled` while preserving visual style.
        */}
        {isCanceledOrInactive ? "Cancelled" : status}
      </span>
    </div>
  );
};

/** Date column – shows single date or date range. If frequency is Hourly, show "Hourly" */
const DateCellRenderer = (params: ICellRendererParams) => {
  const data = params.data as ScheduleRow;
  const dateValue = params.value as string | null | undefined;

  // When frequency indicates hourly, always display "Hourly"
  const freq = String((data && (data.frequency || (data as any).frequecy_name)) || "").toLowerCase();
  if (freq.includes("hour")) {
    return (
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          fontSize: "13px",
        }}
      >
        Hourly
      </span>
    );
  }

  // If date exists, show it
  if (dateValue) {
    return (
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          fontSize: "13px",
        }}
      >
        {dateValue}
      </span>
    );
  }

  // Fallback: show empty placeholder
  return <span style={{ color: "#6B7280" }}>-</span>;
};

/** Type column renderer */
const TypeCellRenderer = (params: ICellRendererParams) => {
  if (!params.value) return <span>-</span>;
  const displayValue = formatScanType(params.value);
  return (
    <Tooltip
      title={displayValue}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #ddd",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
          },
        },
        arrow: { sx: { color: "#fff" } },
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {displayValue}
      </span>
    </Tooltip>
  );
};


/** Reason column with tooltip */
const ReasonCellRenderer = (params: ICellRendererParams) => {
  if (!params.value) return <span style={{ color: "#6B7280" }}>-</span>;
  return (
    <Tooltip
      title={params.value}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #ddd",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
          },
        },
        arrow: { sx: { color: "#fff" } },
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {params.value}
      </span>
    </Tooltip>
  );
};



// ──────────────────────────────────────────────
// Kebab Action Menu for Schedules
// ──────────────────────────────────────────────

export const ScheduleActionMenu = ({
  params,
  onEditSchedule,
  onCancelSchedule,
  onPauseSchedule,
  onResumeSchedule
}: {
  params: ICellRendererParams,
  onEditSchedule?: (data: ScheduleRow) => void,
  onCancelSchedule?: (data: ScheduleRow) => void,
  onPauseSchedule?: (data: ScheduleRow) => void,
  onResumeSchedule?: (data: ScheduleRow) => void
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const data = params.data;
  const statusValue = (data as any)?.scheduledStatus || (data as any)?.schedule?.schedule_status;
  const normalizedStatus = String(statusValue ?? "").trim().toLowerCase();
  const isActive = normalizedStatus === "active";
  const isCancelled = normalizedStatus === "cancelled" || normalizedStatus === "canceled" || normalizedStatus === "inactive";
  const isCompleted = normalizedStatus === "completed";
  const isPaused = normalizedStatus === "pause" || normalizedStatus === "paused";

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeSchedule = () => {
    console.log("Change Schedule:", data);
    onEditSchedule?.(data);
    handleClose();
  };

  const handleCancelSchedule = () => {
    console.log("Cancel Schedule:", data);
    onCancelSchedule?.(data);
    handleClose();
  };

  const handlePauseSchedule = () => {
    console.log("Pause Schedule:", data);
    onPauseSchedule?.(data);
    handleClose();
  };

  const handleResumeSchedule = () => {
    console.log("Resume Schedule:", data);
    onResumeSchedule?.(data);
    handleClose();
  };

  if (!data) return null;
  if (isCancelled || isCompleted) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        data-prevent-row-click="true"
        sx={{ "&:focus": { outline: "none" }, cursor: "pointer" }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
              minWidth: 160,
            },
          },
        }}
      >


        {isPaused && (
          <MenuItem onClick={handleResumeSchedule} sx={{ fontSize: "14px" }}>
            Resume Schedule
          </MenuItem>
        )}

        {isActive && (
          <MenuItem onClick={handleChangeSchedule} sx={{ fontSize: "14px" }}>
            Edit Schedule
          </MenuItem>
        )}
        {isActive && (
          <MenuItem onClick={handlePauseSchedule} sx={{ fontSize: "14px" }}>
            Pause Schedule
          </MenuItem>
        )}
        {isActive && (
          <MenuItem onClick={handleCancelSchedule} sx={{ fontSize: "14px", color: "#f53b3b" }}>
            Cancel Schedule
          </MenuItem>
        )}

        {!isActive && !isCancelled && !isCompleted && !isPaused && (
          <MenuItem onClick={handleChangeSchedule} sx={{ fontSize: "14px" }}>
            Edit Schedule
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

// ──────────────────────────────────────────────
// Hook: useSchedulesColumnDefs
// ──────────────────────────────────────────────

export const useSchedulesColumnDefs = (props?: {
  onEditSchedule?: (data: ScheduleRow) => void,
  onCancelSchedule?: (data: ScheduleRow) => void,
  onPauseSchedule?: (data: ScheduleRow) => void,
  onResumeSchedule?: (data: ScheduleRow) => void
}): ColDef[] => {
  return useMemo(
    () => [
      {
        headerName: "Scan Name",
        field: "scanName",
        filter: true,
        sortable: true,
        minWidth: 120,
        cellRenderer: ScanNameCellRenderer,
      },
      {
        headerName: "Type",
        field: "type",
        filter: true,
        sortable: true,
        minWidth: 160,
        width: 160,
        maxWidth: 250,
        cellRenderer: TypeCellRenderer,
      },
      {
        headerName: "Frequency",
        field: "frequency",
        filter: true,
        sortable: true,
        minWidth: 100,
      },
      {
        headerName: "Created By",
        field: "createdBy",
        filter: true,
        sortable: true,
        minWidth: 140,
        cellRenderer: CreatedByCellRenderer,
      },
      {
        headerName: "Scheduled Time",
        field: "scheduledTime",
        filter: false,
        sortable: true,
        minWidth: 210,
        cellRenderer: ScheduledTimeCellRenderer,
      },
      {
        headerName: "Agent",
        field: "agent",
        filter: true,
        sortable: true,
        minWidth: 120,
      },
      {
        headerName: "Date",
        field: "date",
        filter: false,
        sortable: true,
        minWidth: 160,
        cellRenderer: DateCellRenderer,
        comparator: (valueA: string, valueB: string, nodeA: any, nodeB: any, isInverted: boolean) => {
          const aTime = valueA ? new Date(valueA).getTime() : NaN;
          const bTime = valueB ? new Date(valueB).getTime() : NaN;
          const aValid = !isNaN(aTime) && isFinite(aTime);
          const bValid = !isNaN(bTime) && isFinite(bTime);

          const getStringPriority = (val: string, node: any): number => {
            const str = String(val ?? "").trim().toLowerCase();
            const freq = String(node.data?.frequency ?? node.data?.frequecy_name ?? "").toLowerCase();

            if (!val && freq.includes("hour")) return 1; // Hourly (null date)
            if (str === "everyday") return 2;
            if (str === "sunday") return 3;
            if (str === "monday") return 4;
            if (str === "tuesday") return 5;
            if (str === "wednesday") return 6;
            if (str === "thursday") return 7;
            if (str === "friday") return 8;
            if (str === "saturday") return 9;
            return -1; // real date
          };

          const pA = getStringPriority(valueA, nodeA);
          const pB = getStringPriority(valueB, nodeB);

          const aIsString = pA !== -1;
          const bIsString = pB !== -1;

          // Both are real dates → sort oldest first, AG Grid flips for DESC
          if (!aIsString && !bIsString) {
            return aTime - bTime;
          }

          // Both are strings → sort by priority, AG Grid flips for DESC
          if (aIsString && bIsString) {
            if (pA !== pB) return pA - pB;
            // tiebreak by date_added
            const aAdded = nodeA.data?.date_added ? new Date(nodeA.data.date_added).getTime() : 0;
            const bAdded = nodeB.data?.date_added ? new Date(nodeB.data.date_added).getTime() : 0;
            return aAdded - bAdded;
          }

          // Mixed: date vs string
          // Dates always first → return negative if A is date, AG Grid flips for DESC
          return aIsString ? 1 : -1;
        },
      },
      {
        headerName: "Reason",
        field: "reason",
        filter: true,
        sortable: true,
        minWidth: 150,
        cellRenderer: ReasonCellRenderer,
      },
      {
        headerName: "",
        field: "actions",
        filter: false,
        sortable: false,
        width: 50,
        maxWidth: 50,
        cellRenderer: (params: ICellRendererParams) => {
          if (!params.data) return null;
          return <ScheduleActionMenu params={params}
            onEditSchedule={props?.onEditSchedule}
            onCancelSchedule={props?.onCancelSchedule}
            onPauseSchedule={props?.onPauseSchedule}
            onResumeSchedule={props?.onResumeSchedule}
          />;
        },
      },
    ],
    [props?.onEditSchedule, props?.onCancelSchedule, props?.onPauseSchedule, props?.onResumeSchedule]
  );
};
